import React from 'react';
import { Banknote, CheckCircle2, Phone, Truck } from 'lucide-react';
import { phoneInternational } from '@xshop/shared/src/formatters/phone';
import type { ShellOrder } from '../themeHost/themeMount';
import { isThemeEditor } from '../editor/editorVisit';
import { isThemePreview } from '../api/previewVisit';
import { swatchName } from '../elements/render/colorSwatch';
import { useCommerce } from './ShopCommerce';
import { cartMoney } from './cartMoney';
import { confirmationHeading } from './confirmationHeading';
import './orderConfirmation.css';
import { orderWords, type OrderWords } from './orderWords';
import { shopWords, type ShopWords } from './shopWords';
import { paymentWords, type PaymentWords } from './paymentWords';
import { useCardReturn } from './useCardReturn';
import { CardPaymentPending } from './CardPaymentPending';
import { CheckoutTotals } from '../components/CheckoutTotals';
import { checkoutLanguage, resolveCheckoutLabels, type CheckoutLabels, type CheckoutLanguage } from '../components/checkoutLabels';
import { DISCOUNT_CODE_WORDS } from '../components/discountCodeWords';
import { offerSavings } from './offerSavings';

// The thank-you page as Shopify's order status page opens, said for a cash-on-delivery order: the order's
// number and the buyer's name, that it is confirmed and what happens now, then the details they gave. It
// only ever shows the order placed on this visit; the builder and a theme preview show a sample instead,
// so the page can be designed before anyone has bought anything.

/** What the builder and a theme preview confirm, plainly a sample. */
const SAMPLE_ORDER: ShellOrder = {
  orderNumber: '#1001', customerFullName: 'Sara Benali', customerPhone: '06 12 34 56 78', shippingCity: 'Casablanca',
  shippingAddress: '12 Rue Atlas, Maârif', totalPrice: 448.2, subtotalPrice: 448.2, discountAmount: 0, shippingPrice: 0, paymentMethod: 'COD', currency: 'MAD',
  lines: [{ title: 'Sample product', option: 'Medium', quantity: 2, total: 268.2 }, { title: 'Second sample product', option: '', quantity: 1, total: 180 }]
};

/** The icons of the three steps a cash-on-delivery order goes through after it is placed. */
const STEP_ICONS = [Phone, Truck, Banknote] as const;

type OrderConfirmationProps = { heading?: string; showSteps?: boolean };

/** The confirmation's words and the storefront's, in the store's language. */
type Words = { order: OrderWords; shop: ShopWords; pay: PaymentWords };

// Says how the order is paid for, in the words a buyer reads on a receipt.
const paymentLabel = (method: string, words: Words): string => {
  const kind = method.toUpperCase();
  return kind === 'COD' ? words.order.cashOnDelivery : kind === 'STRIPE' ? words.pay.card : method;
};

// Draws what happens after a cash-on-delivery order, one numbered step at a time.
const OrderSteps: React.FC<{ words: OrderWords }> = ({ words }) => (
  <ol className="xe-order-steps" aria-label={words.whatNext}>
    {words.steps.map(({ title, text }, index) => {
      const Icon = STEP_ICONS[index] ?? Phone;
      return <li key={title}><Icon size={20} aria-hidden="true" /><span><strong>{title}</strong> {text}</span></li>;
    })}
  </ol>
);

// Draws the details the buyer gave, two by two, with what they will pay.
const OrderDetails: React.FC<{ order: ShellOrder; total: string; words: Words }> = ({ order, total, words }) => (
  <section className="xe-order-card" aria-label={words.shop.orderDetails}>
    <h2 className="xe-order-card-title">{words.shop.orderDetails}</h2>
    <dl className="xe-order-details">
      <div><dt>{words.shop.contact}</dt><dd>{order.customerFullName}<br /><span className="xe-order-phone" dir="ltr">{phoneInternational(order.customerPhone)}</span></dd></div>
      <div><dt>{words.shop.deliveryAddress}</dt><dd>{order.shippingAddress}<br />{order.shippingCity}</dd></div>
      <div><dt>{words.shop.payment}</dt><dd>{paymentLabel(order.paymentMethod, words)}</dd></div>
      <div><dt>{words.shop.total}</dt><dd><strong>{total}</strong></dd></div>
    </dl>
  </section>
);

// The order's money as the checkout summed it, as Shopify's order status page recaps it under the items: the subtotal
// at full price, each pack's saving, the discount, the delivery and the total — the checkout's own rows and words.
const OrderTotals: React.FC<{ order: ShellOrder; lines: NonNullable<ShellOrder['lines']>; money: (amount: number) => string; labels: CheckoutLabels; language: CheckoutLanguage }> = ({ order, lines, money, labels, language }) => {
  if (order.subtotalPrice === undefined) return null;
  // A discount the checkout named keeps that name; one it did not is "Discount" in the store's language.
  const discountName = order.discountLabel || DISCOUNT_CODE_WORDS[language].row.replace(/\s*\(\{code\}\)/, '');
  return (
    <CheckoutTotals quantity={lines.reduce((sum, line) => sum + line.quantity, 0)} merchandiseTotal={order.subtotalPrice}
      offers={offerSavings(lines, 'Pack')} bump={null} shippingFee={order.shippingPrice ?? null} total={Number(order.totalPrice) || 0}
      discount={order.discountAmount ? { label: discountName, amount: order.discountAmount } : null}
      format={money} labels={labels} />
  );
};

// Lists what the order held — each line's picture, name, option, how many and what it came to — then what it came to.
const OrderItems: React.FC<{ order: ShellOrder; lines: NonNullable<ShellOrder['lines']>; money: (amount: number) => string; words: Words; labels: CheckoutLabels; language: CheckoutLanguage }> = ({ order, lines, money, words, labels, language }) => (
  <section className="xe-order-card" aria-label={words.shop.items}>
    <h2 className="xe-order-card-title">{words.shop.items}</h2>
    <ul className="xe-order-items">
      {lines.map((line, index) => (
        <li key={`${line.title}-${index}`}>
          <span className="xe-order-item-media">{line.image ? <img src={line.image} alt="" /> : null}<span className="xe-order-item-count">{line.quantity}</span></span>
          <span className="xe-order-item-words"><span dir="auto">{line.title}</span>{line.option ? <small>{swatchName(line.option)}</small> : null}</span>
          <strong>{money(line.total)}</strong>
        </li>
      ))}
    </ul>
    <OrderTotals order={order} lines={lines} money={money} labels={labels} language={language} />
  </section>
);

// Says there is no order on this visit to confirm, and where its confirmation will appear.
const NoOrder: React.FC<{ words: OrderWords }> = ({ words }) => (
  <div className="xe-order-empty">
    <h1 className="xe-order-card-title">{words.noOrderTitle}</h1>
    <p>{words.noOrderText}</p>
  </div>
);

/** The order a confirmation shows, with its total already spelled in the shop's currency, and how to spell more. */
type Confirmed = { order: ShellOrder; total: string; cash: boolean; money: (amount: number) => string };

// Reads the order to confirm and what it comes to: this visit's own, or the sample a builder designs against.
function useConfirmedOrder(): Confirmed | null {
  const commerce = useCommerce();
  const order = isThemeEditor() || isThemePreview() ? SAMPLE_ORDER : commerce?.order ?? null;
  if (!order) return null;
  const currency = order.currency ?? commerce?.currency ?? 'MAD';
  // Spells an amount in the order's currency, in the page's reading direction.
  const money = (amount: number) => cartMoney(amount, currency, commerce?.direction ?? 'ltr');
  return { order, total: money(Number(order.totalPrice) || 0), cash: order.paymentMethod.toUpperCase() === 'COD', money };
}

// Draws the card that says the order is confirmed, how it will be checked, and how it is paid for.
const ConfirmedCard: React.FC<{ confirmed: Confirmed; showSteps: boolean; words: Words }> = ({ confirmed, showSteps, words }) => {
  const [payBefore, payAfter] = confirmed.cash ? words.order.payCash : words.order.paymentRecorded;
  return (
    <section className="xe-order-card" aria-label={words.shop.orderConfirmed}>
      <h2 className="xe-order-card-title">{words.shop.orderConfirmed}</h2>
      <p>{words.order.callYou[0]}<strong className="xe-order-phone" dir="ltr">{phoneInternational(confirmed.order.customerPhone)}</strong>{words.order.callYou[1]}{' '}{payBefore}<strong>{confirmed.total}</strong>{payAfter}</p>
      {showSteps && confirmed.cash ? <OrderSteps words={words.order} /> : null}
      <a className="xe-order-track" href={`/account?order=${encodeURIComponent(confirmed.order.orderNumber)}`}>{words.shop.trackOrder}</a>
    </section>
  );
};

// Confirms the order placed on this visit, or a sample one in the builder and a theme preview.
export const OrderConfirmation: React.FC<OrderConfirmationProps> = ({ heading, showSteps = true }) => {
  const confirmed = useConfirmedOrder();
  const language = useCommerce()?.language;
  const card = useCardReturn();
  const words: Words = { order: orderWords(language), shop: shopWords(language), pay: paymentWords(language) };
  const checkoutLang = checkoutLanguage(language, useCommerce()?.direction ?? 'ltr');
  const labels = resolveCheckoutLabels(undefined, checkoutLang);
  // Back from Stripe, the order is confirmed only once its payment is; until then the page says where it stands.
  if (!confirmed) return card.stage === 'none' ? <NoOrder words={words.order} /> : <CardPaymentPending state={card} language={language} />;
  const { order, total } = confirmed;
  return (
    <div className="xe-order">
      <header className="xe-order-head" role="status">
        <CheckCircle2 className="xe-order-check" size={44} aria-hidden="true" />
        <div><p className="xe-order-number">{words.order.orderNumber.replace('{number}', order.orderNumber)}</p><h1 className="xe-order-title">{confirmationHeading(heading ?? words.order.thankYou, order.customerFullName)}</h1></div>
      </header>
      <ConfirmedCard confirmed={confirmed} showSteps={showSteps} words={words} />
      {order.lines?.length ? <OrderItems order={order} lines={order.lines} money={confirmed.money} words={words} labels={labels} language={checkoutLang} /> : null}
      <OrderDetails order={order} total={total} words={words} />
    </div>
  );
};
